import { ISearchElementStyle } from './searchPageConfig';
import { ILayout } from './layout';

// Custom header links: centered in the header bar's empty space, spreading out from the center.
// Persisted as a JSON string on IStorefrontSettings.headerLinksConfig and authored in the ac-main admin.

export interface IHeaderLink {
    id?: string;
    label?: string;
    url?: string;
    iconUrl?: string;          // uploaded image blob URL, shown left of the label
    newTab?: boolean;          // open in a new tab
    style?: ISearchElementStyle; // per-link font/type/color/background/border/padding
    // Nested dropdown menu items (AC-934/Stage 1). A link with children renders as a dropdown
    // trigger. Recursive; additive — absent ⇒ a plain link exactly as today.
    children?: IHeaderLink[];
    // Rich hover flyout / mega-menu (Stage 5): the panel content is itself a grid layout.
    panel?: ILayout;
    // Render the panel as a full-width bar spanning the viewport, dropping just below the header,
    // instead of a popover anchored under the trigger (AC-968). Panel-only; absent ⇒ popover.
    panelFullWidth?: boolean;
    // How the dropdown/panel opens. Default: hover on desktop, tap on touch.
    trigger?: 'hover' | 'click';
}

export interface IHeaderLinksConfig {
    container?: ISearchElementStyle; // styling for the centered links container
    cellStyle?: ISearchElementStyle; // default styling applied to every link cell/chip
    // How the links sit in the space between the logo and the header actions. Defaults to 'center'.
    align?: 'left' | 'center' | 'right';
    links?: IHeaderLink[];
    // Links rendered in the header ACTIONS area (right side, just left of cart/account) instead of the
    // centered nav row — e.g. a Support link + a Contact CTA (AC-970). Same IHeaderLink shape.
    actionLinks?: IHeaderLink[];
    // The surface of the dropdown that a link with `children` opens (background / border / radius /
    // shadow). Absent ⇒ the dropdown inherits headerConfig.menu — the same surface as the built-in
    // category menu — and failing that a luminance-aware default derived from the header bar, so it
    // is never a white slab under a dark header. Set this only to make the nav dropdown differ.
    dropdown?: ISearchElementStyle;
    [prop: string]: unknown;
}

// Map the header-links alignment to a flex justify-content value (default centered).
export const headerLinksJustify = (align?: 'left' | 'center' | 'right'): 'flex-start' | 'center' | 'flex-end' =>
    align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center';

export function parseHeaderLinksConfig(raw?: string): IHeaderLinksConfig {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? (parsed as IHeaderLinksConfig) : {};
    } catch {
        return {};
    }
}

// An href is external when it has a scheme/protocol-relative prefix; otherwise it's an in-app route.
export const isExternalUrl = (url?: string): boolean =>
    !!url && (/^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith('//'));

// tel:/mailto: links aren't navigable pages at all — they only make sense as a plain native
// anchor (already true of every external link, since isExternalUrl's scheme test matches these
// schemes too) and should never open in a new tab, even if a link is misconfigured with newTab
// (a phone/mail-client handoff popping a blank tab is just noise, not a real navigation).
export const isNonNavigableUrl = (url?: string): boolean =>
    !!url && /^(tel|mailto):/i.test(url);
